/**
 * Styled XLSX export for the travel Cari Mizan — built with exceljs
 * (lazy-loaded) so the workbook carries real design: letterhead block,
 * navy header band, currency/type group bands, number formats, borders,
 * bold totals and a colored signed BAKİYE line. Numbers are written as
 * numbers (not text) with '#,##0.00' formats so Excel can keep computing.
 */

import type { MizanPdfCurrencySection } from '@/components/travel/cari/cari-mizan-pdf';

export interface MizanXlsxMeta {
  title: string;
  periodLabel: string;
  generatedLabel: string;
  params: { label: string; value: string }[];
  columns: { no: string; code: string; name: string; debit: string; credit: string; debitBalance: string; creditBalance: string };
  totalLabel: string;
  balanceLabel: string;
  debitSide: string;
  creditSide: string;
  currencyNames: Record<string, string>;
  typeLabel: (code: string) => string;
}

const NAVY = 'FF1E3A5F';
const NAVY_SOFT = 'FFEEF2F7';
const BAND = 'FFF3F4F6';
const INK = 'FF111827';
const INK_SOFT = 'FF4B5563';
const WHITE = 'FFFFFFFF';
const GREEN = 'FF047857';
const RED = 'FFB91C1C';
const LINE = 'FFE5E7EB';

const NUM_FMT = '#,##0.00;[Red]-#,##0.00';

/** Build the styled workbook (shared by the browser export and Node tests). */
export async function buildCariMizanWorkbook(
  data: { fiscalYear: number; currencies: MizanPdfCurrencySection[] },
  meta: MizanXlsxMeta
) {
  const mod = (await import('exceljs')) as typeof import('exceljs') & { default?: typeof import('exceljs') };
  const ExcelJS = mod.default ?? mod;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Polatkan Admin';
  workbook.created = new Date();

  const ws = workbook.addWorksheet(`Cari Mizan ${data.fiscalYear}`, {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  ws.columns = [
    { width: 6 }, // No
    { width: 12 }, // Cari No
    { width: 46 }, // Ünvan
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
  ];

  const thin = { style: 'thin' as const, color: { argb: LINE } };
  const numCols = [4, 5, 6, 7];

  // ── Letterhead ─────────────────────────────────────────────────────────
  ws.mergeCells('A1:G1');
  const titleCell = ws.getCell('A1');
  titleCell.value = 'POLATKAN TURİZM';
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: NAVY } };
  ws.getRow(1).height = 24;

  ws.mergeCells('A2:G2');
  const subCell = ws.getCell('A2');
  subCell.value = `${meta.title} · ${data.fiscalYear} · ${meta.periodLabel}`;
  subCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: INK } };

  ws.mergeCells('A3:G3');
  const paramCell = ws.getCell('A3');
  paramCell.value = meta.params.map((p) => `${p.label}: ${p.value}`).join('   ·   ');
  paramCell.font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };

  ws.mergeCells('A4:G4');
  ws.getCell('A4').value = meta.generatedLabel;
  ws.getCell('A4').font = { name: 'Calibri', size: 8, italic: true, color: { argb: INK_SOFT } };

  ws.addRow([]);

  // ── Sections ───────────────────────────────────────────────────────────
  for (const cur of data.currencies) {
    // Currency band
    const bandRow = ws.addRow([`${meta.currencyNames[cur.currency] ?? cur.currency}`, '', '', '', '', '', cur.currency]);
    ws.mergeCells(`A${bandRow.number}:F${bandRow.number}`);
    bandRow.height = 18;
    bandRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
      cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: WHITE } };
      cell.alignment = { vertical: 'middle' };
    });
    bandRow.getCell(7).alignment = { vertical: 'middle', horizontal: 'right' };

    // Column header
    const header = ws.addRow([
      meta.columns.no,
      meta.columns.code,
      meta.columns.name,
      meta.columns.debit,
      meta.columns.credit,
      meta.columns.debitBalance,
      meta.columns.creditBalance,
    ]);
    header.height = 16;
    header.eachCell((cell, col) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_SOFT } };
      cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: NAVY } };
      cell.border = { bottom: { style: 'medium', color: { argb: NAVY } } };
      if (numCols.includes(col)) cell.alignment = { horizontal: 'right' };
    });

    let rowNo = 0;
    for (const group of cur.cariGroups) {
      // Group band
      const gRow = ws.addRow([`${group.cariType} — ${meta.typeLabel(group.cariType).toLocaleUpperCase('tr-TR')}`]);
      ws.mergeCells(`A${gRow.number}:G${gRow.number}`);
      gRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BAND } };
      gRow.getCell(1).font = { name: 'Calibri', size: 8.5, bold: true, color: { argb: INK_SOFT } };

      // Rows
      for (const r of group.rows) {
        rowNo += 1;
        const row = ws.addRow([rowNo, r.cariCode, r.companyName, r.debit, r.credit, r.debitBalance, r.creditBalance]);
        row.getCell(1).font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };
        row.getCell(2).font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };
        row.getCell(3).font = { name: 'Calibri', size: 9, color: { argb: INK } };
        for (const col of numCols) {
          const cell = row.getCell(col);
          cell.numFmt = NUM_FMT;
          cell.font = { name: 'Calibri', size: 9, color: { argb: INK } };
          cell.alignment = { horizontal: 'right' };
        }
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = { bottom: thin };
        });
      }

      // Group totals
      const tRow = ws.addRow([
        '',
        '',
        `${group.cariType} ${meta.totalLabel}`,
        group.totals.debit,
        group.totals.credit,
        group.totals.debitBalance,
        group.totals.creditBalance,
      ]);
      tRow.eachCell({ includeEmpty: true }, (cell, col) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_SOFT } };
        cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: INK } };
        if (numCols.includes(col)) {
          cell.numFmt = NUM_FMT;
          cell.alignment = { horizontal: 'right' };
        }
      });
    }

    // Currency totals
    const cRow = ws.addRow([
      '',
      '',
      `${cur.currency} ${meta.totalLabel}`,
      cur.totals.debit,
      cur.totals.credit,
      cur.totals.debitBalance,
      cur.totals.creditBalance,
    ]);
    cRow.height = 17;
    cRow.eachCell({ includeEmpty: true }, (cell, col) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
      cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: WHITE } };
      if (numCols.includes(col)) {
        cell.numFmt = NUM_FMT;
        cell.alignment = { horizontal: 'right' };
      }
    });

    // Signed balance line
    const net = cur.totals.net;
    const bRow = ws.addRow(['', '', '', '', '', `${meta.balanceLabel} ${net >= 0 ? meta.debitSide : meta.creditSide}`, Math.abs(net)]);
    bRow.getCell(6).font = { name: 'Calibri', size: 10, bold: true, color: { argb: net >= 0 ? GREEN : RED } };
    bRow.getCell(6).alignment = { horizontal: 'right' };
    const bCell = bRow.getCell(7);
    bCell.numFmt = NUM_FMT;
    bCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: net >= 0 ? GREEN : RED } };
    bCell.alignment = { horizontal: 'right' };
    bRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { bottom: { style: 'double', color: { argb: LINE } } };
    });

    ws.addRow([]);
  }

  return workbook;
}

/** Browser entry point: build the workbook and trigger a download. */
export async function exportCariMizanXlsx(
  data: { fiscalYear: number; currencies: MizanPdfCurrencySection[] },
  meta: MizanXlsxMeta,
  filename: string
): Promise<void> {
  const workbook = await buildCariMizanWorkbook(data, meta);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
